import * as React from "react";
import type { InstallPromptPlatform } from "@app/ui";
import { detectPlatform } from "./Platform";

// beforeinstallprompt 는 표준 lib.dom 타입에 없어(비표준, Chromium 계열만) 직접 정의한다.
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "withtask.install-prompt.dismissed-until";
// 닫거나("나중에"/X) 설치가 끝나면 이만큼 다시 띄우지 않는다(재노출 정책: 14일).
const DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000;

// ── localStorage 헬퍼 (Theme.ts / AuthBootstrap.ts 의 try-catch 폴백 패턴) ──
// 사생활 보호 모드 등에서 접근이 막혀도 조용히 "안 닫음"으로 폴백한다.
function readDismissedUntil(): number {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const ts = raw ? Number(raw) : 0;
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
}

function snoozeFor(ms: number): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + ms));
  } catch {
    // 저장 실패 시 이 세션 플래그(dismissed)만으로 숨김을 유지한다.
  }
}

// ── 설치 여부 / 플랫폼 감지 (DevInfoModal.tsx 의 standalone 감지와 동일 기준) ──
function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ 는 데스크톱 Safari UA 를 쓰므로 터치 지원 Mac(=iPad)도 iOS 로 본다.
  const iPadOS =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

// ── 모듈 스코프 스토어 (Toast.tsx 와 동일한 useSyncExternalStore 방식) ──
// beforeinstallprompt 는 React 트리가 마운트되기 전에 발생할 수 있어, 이벤트를
// 놓치지 않으려면 부팅 시점(main.tsx)에 등록해 모듈 변수에 잡아둬야 한다.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
// 이 페이지 로드 동안 닫혔거나(나중에/X) 설치가 끝나 더는 안 띄우는 상태.
let dismissedThisLoad = false;
let version = 0;
const listeners = new Set<() => void>();

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): number {
  return version;
}

/**
 * 부팅 시 1회 호출(main.tsx). beforeinstallprompt 를 가로채 브라우저 기본 미니
 * 인포바를 막고, 커스텀 배너에서 쓸 수 있게 이벤트를 보관한다.
 */
export function initInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    // 기본 미니 인포바를 억제하고 우리 배너로 유도한다.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emit();
  });

  // 설치가 완료되면(우리 배너·브라우저 UI 어느 쪽이든) 배너를 내리고 재노출을 미룬다.
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    dismissedThisLoad = true;
    snoozeFor(DISMISS_DURATION_MS);
    emit();
  });
}

export interface InstallPromptState {
  /** 배너를 지금 노출해야 하는지. */
  visible: boolean;
  /** 노출 형태 — 원터치 설치(default) 또는 iOS 수동 안내(ios). */
  mode: InstallPromptPlatform;
  /** 원터치 설치 트리거(default 모드). */
  install: () => void;
  /** 닫기/나중에 — 14일 뒤 재노출. */
  dismiss: () => void;
}

async function install(): Promise<void> {
  const prompt = deferredPrompt;
  if (!prompt) return;
  // prompt() 는 한 이벤트당 1회만 유효하므로 즉시 소진 처리한다.
  deferredPrompt = null;
  emit();
  try {
    await prompt.prompt();
    const choice = await prompt.userChoice;
    // 설치를 거부해도 곧바로 다시 조르지 않도록 재노출을 미룬다.
    // (수락 시엔 appinstalled 가 별도로 처리한다.)
    if (choice.outcome === "dismissed") {
      dismissedThisLoad = true;
      snoozeFor(DISMISS_DURATION_MS);
      emit();
    }
  } catch {
    // 프롬프트 표시 실패 — 다음 기회에 다시 시도할 수 있게 조용히 넘어간다.
  }
}

function dismiss(): void {
  dismissedThisLoad = true;
  snoozeFor(DISMISS_DURATION_MS);
  emit();
}

function computeState(): InstallPromptState {
  const eligible =
    detectPlatform() === "web" && // 네이티브(Capacitor)·데스크톱(Electron) 제외
    !dismissedThisLoad &&
    !isStandalone() && // 이미 설치돼 standalone 으로 실행 중이면 숨김
    readDismissedUntil() < Date.now(); // 14일 스누즈 기간

  // 원터치 설치가 가능한(beforeinstallprompt 를 받은) 브라우저면 default,
  // 아니면 iOS Safari 에 한해 수동 안내를 보여준다. 둘 다 아니면 노출하지 않는다.
  let mode: InstallPromptPlatform | null = null;
  if (deferredPrompt) mode = "default";
  else if (isIos()) mode = "ios";

  return {
    visible: eligible && mode !== null,
    mode: mode ?? "default",
    install,
    dismiss,
  };
}

/** 배너 렌더 상태를 구독한다. beforeinstallprompt/설치/닫기에 따라 갱신된다. */
export function useInstallPrompt(): InstallPromptState {
  React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return computeState();
}
